import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type DuelStatusFilter, type PuzzleQuery, type RoomStatusFilter } from "./client";
import type { JsonValue, LeaderboardName, PuzzleStatus, ReportPatch } from "./schemas";

export type TeamStatusFilter = "active" | "dormant" | "disbanded" | null;

export const keys = {
  me: ["me"] as const,
  dashboard: (date?: string) => ["dashboard", date ?? "today"] as const,
  puzzles: (q: PuzzleQuery) => ["puzzles", q] as const,
  puzzle: (id: string) => ["puzzle", id] as const,
  schedule: (from: string, to: string) => ["schedule", from, to] as const,
  tracks: ["levels", "tracks"] as const,
  pack: (track: string, index: number) => ["levels", "pack", track, index] as const,
  players: (q: string) => ["players", q] as const,
  player: (id: string) => ["player", id] as const,
  integrity: (date: string) => ["integrity", date] as const,
  config: ["config"] as const,
  audit: ["audit"] as const,
  paywall: (from: string, to: string) => ["paywall", from, to] as const,
  badges: ["badges"] as const,
  leaderboard: (board: LeaderboardName, n: number | null) => ["leaderboard", board, n] as const,
  reports: (status: "open" | "closed") => ["reports", status] as const,
  teams: (q: string, status: TeamStatusFilter) => ["teams", q, status] as const,
  team: (id: string) => ["team", id] as const,
  divisions: (week: string | null) => ["divisions", week] as const,
  arenaDrops: (from: string, to: string) => ["arena", "drops", from, to] as const,
  arenaMatch: (id: string) => ["arena", "match", id] as const,
  duels: (status: DuelStatusFilter) => ["duels", status] as const,
  duel: (id: string) => ["duel", id] as const,
  rooms: (q: string, status: RoomStatusFilter) => ["rooms", q, status] as const,
  room: (id: string) => ["room", id] as const,
};

export const useDashboard = (date?: string) =>
  useQuery({ queryKey: keys.dashboard(date), queryFn: ({ signal }) => api.dashboard(date, signal) });

export const usePuzzles = (q: PuzzleQuery) =>
  useQuery({
    queryKey: keys.puzzles(q),
    queryFn: ({ signal }) => api.puzzles(q, signal),
    placeholderData: keepPreviousData,
  });

export const usePuzzle = (id: string | undefined) =>
  useQuery({
    queryKey: keys.puzzle(id ?? ""),
    queryFn: ({ signal }) => api.puzzle(id!, signal),
    enabled: Boolean(id),
  });

export const useSchedule = (from: string, to: string) =>
  useQuery({
    queryKey: keys.schedule(from, to),
    queryFn: ({ signal }) => api.schedule(from, to, signal),
    placeholderData: keepPreviousData,
  });

export const useTracks = () => useQuery({ queryKey: keys.tracks, queryFn: ({ signal }) => api.tracks(signal) });

export const usePack = (track: string | undefined, index: number | undefined) =>
  useQuery({
    queryKey: keys.pack(track ?? "", index ?? -1),
    queryFn: ({ signal }) => api.pack(track!, index!, signal),
    enabled: Boolean(track) && index !== undefined && Number.isInteger(index),
    staleTime: 5 * 60_000,
  });

export const usePlayers = (q: string) =>
  useQuery({
    queryKey: keys.players(q),
    queryFn: ({ signal }) => api.players(q, signal),
    placeholderData: keepPreviousData,
  });

export const usePlayer = (id: string | undefined) =>
  useQuery({ queryKey: keys.player(id ?? ""), queryFn: ({ signal }) => api.player(id!, signal), enabled: Boolean(id) });

export const useIntegrity = (date: string) =>
  useQuery({
    queryKey: keys.integrity(date),
    queryFn: ({ signal }) => api.integrity(date, signal),
    placeholderData: keepPreviousData,
  });

export const useConfig = () => useQuery({ queryKey: keys.config, queryFn: ({ signal }) => api.config(signal) });

export const usePaywall = (from: string, to: string) =>
  useQuery({ queryKey: keys.paywall(from, to), queryFn: ({ signal }) => api.paywall(from, to, signal), placeholderData: keepPreviousData });

export const useBadges = () => useQuery({ queryKey: keys.badges, queryFn: ({ signal }) => api.badges(signal) });

export const useLeaderboard = (board: LeaderboardName, n: number | null) =>
  useQuery({
    queryKey: keys.leaderboard(board, n),
    queryFn: ({ signal }) => api.leaderboard(board, n, signal),
    placeholderData: keepPreviousData,
  });

export const useReports = (status: "open" | "closed") =>
  useInfiniteQuery({
    queryKey: keys.reports(status),
    queryFn: ({ pageParam, signal }) => api.reports(status, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

export const useTeams = (q: string, status: TeamStatusFilter = null) =>
  useQuery({ queryKey: keys.teams(q, status), queryFn: ({ signal }) => api.teams(q, status, signal), placeholderData: keepPreviousData });

export const useTeam = (id: string | undefined) =>
  useQuery({ queryKey: keys.team(id ?? ""), queryFn: ({ signal }) => api.team(id!, signal), enabled: Boolean(id) });

export const useDivisions = (week: string | null) =>
  useQuery({ queryKey: keys.divisions(week), queryFn: ({ signal }) => api.divisions(week, signal), placeholderData: keepPreviousData });

export const useArenaDrops = (from: string, to: string) =>
  useQuery({ queryKey: keys.arenaDrops(from, to), queryFn: ({ signal }) => api.arenaDrops(from, to, signal), placeholderData: keepPreviousData, refetchInterval: 60_000 });

/** A finished match never changes. */
export const useArenaMatch = (id: string | undefined) =>
  useQuery({ queryKey: keys.arenaMatch(id ?? ""), queryFn: ({ signal }) => api.arenaMatch(id!, signal), enabled: Boolean(id), staleTime: Infinity });

export const useDuels = (status: DuelStatusFilter) =>
  useQuery({ queryKey: keys.duels(status), queryFn: ({ signal }) => api.duels(status, undefined, signal), placeholderData: keepPreviousData });

export const useDuel = (id: string | undefined) =>
  useQuery({ queryKey: keys.duel(id ?? ""), queryFn: ({ signal }) => api.duel(id!, signal), enabled: Boolean(id) });

export const useRooms = (q: string, status: RoomStatusFilter) =>
  useQuery({ queryKey: keys.rooms(q, status), queryFn: ({ signal }) => api.rooms(q, status, signal), placeholderData: keepPreviousData });

export const useRoom = (id: string | undefined) =>
  useQuery({ queryKey: keys.room(id ?? ""), queryFn: ({ signal }) => api.room(id!, signal), enabled: Boolean(id) });

export const useAudit = () =>
  useInfiniteQuery({
    queryKey: keys.audit,
    queryFn: ({ pageParam, signal }) => api.audit(pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

// ---------------------------------------------------------------- mutations

/** Everything a content mutation can change. */
function useInvalidateContent() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["puzzles"] }),
      qc.invalidateQueries({ queryKey: ["puzzle"] }),
      qc.invalidateQueries({ queryKey: ["schedule"] }),
      qc.invalidateQueries({ queryKey: ["dashboard"] }),
      qc.invalidateQueries({ queryKey: keys.audit }),
    ]);
}

export function useSetPuzzleStatus() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PuzzleStatus }) => api.setPuzzleStatus(id, status),
    onSuccess: invalidate,
  });
}

export function useSwapDays() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: ({ dateA, dateB }: { dateA: string; dateB: string }) => api.swapDays(dateA, dateB),
    onSuccess: invalidate,
  });
}

export function useApproveRange() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => api.approveRange(from, to),
    onSuccess: invalidate,
  });
}

export function useResetHandle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.resetHandle(id),
    onSuccess: (detail, id) =>
      Promise.all([
        qc.setQueryData(keys.player(id), detail),
        qc.invalidateQueries({ queryKey: ["players"] }),
        qc.invalidateQueries({ queryKey: keys.audit }),
      ]),
  });
}

/** PATCH /config: several keys in one call (all or nothing). */
export function usePatchConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, JsonValue>) => api.patchConfig(values),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: keys.config }), qc.invalidateQueries({ queryKey: keys.audit })]),
  });
}

export function useSetConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: JsonValue }) => api.setConfig(key, value),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: keys.config }), qc.invalidateQueries({ queryKey: keys.audit })]),
  });
}

/** Pro grant / revoke (admin only): refresh the player, the audit log and the monetisation numbers. */
function useInvalidatePlayer() {
  const qc = useQueryClient();
  return (id: string) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.player(id) }),
      qc.invalidateQueries({ queryKey: keys.audit }),
      qc.invalidateQueries({ queryKey: ["paywall"] }),
    ]);
}

export function useGrantPro() {
  const invalidate = useInvalidatePlayer();
  return useMutation({
    mutationFn: ({ id, days, reason }: { id: string; days: number; reason: string }) => api.grantPro(id, days, reason),
    onSuccess: (_d, v) => invalidate(v.id),
  });
}

export function useRevokeProGrant() {
  const invalidate = useInvalidatePlayer();
  return useMutation({ mutationFn: (id: string) => api.revokeProGrant(id), onSuccess: (_d, id) => invalidate(id) });
}

/** Resolving a report can reset a handle or hide a player from boards: refresh everything that shows either. */
export function useUpdateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ReportPatch }) => api.updateReport(id, patch),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["reports"] }),
        qc.invalidateQueries({ queryKey: ["player"] }),
        qc.invalidateQueries({ queryKey: ["players"] }),
        qc.invalidateQueries({ queryKey: ["leaderboard"] }),
        qc.invalidateQueries({ queryKey: keys.audit }),
      ]),
  });
}

/** Team moderation changes names, rosters and tables: refresh every team view, players and the audit log. */
function useInvalidateTeams() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["teams"] }),
      qc.invalidateQueries({ queryKey: ["team"] }),
      qc.invalidateQueries({ queryKey: ["divisions"] }),
      qc.invalidateQueries({ queryKey: ["player"] }),
      qc.invalidateQueries({ queryKey: keys.audit }),
    ]);
}

export function useResetTeamName() {
  const invalidate = useInvalidateTeams();
  return useMutation({ mutationFn: (id: string) => api.resetTeamName(id), onSuccess: invalidate });
}

export function useDisbandTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({ mutationFn: (id: string) => api.disbandTeam(id), onSuccess: invalidate });
}

/** The teams close: a dry run changes nothing; the real run refreshes the tables, teams and audit log. */
export function useTeamsClose() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ week, dryRun, token }: { week: string; dryRun: boolean; token?: string | null }) => api.teamsClose(week, dryRun, token),
    onSuccess: (result) => (result.dryRun ? undefined : invalidate()),
  });
}
